import { Stack, Separator, getTheme, Text, DefaultButton } from '@fluentui/react';
import beautify from 'json-beautify';
import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { FieldSectionComponent } from '../FieldSectionComponent';
import { BaseTab } from './BaseTab';
import { separatorStyle } from '../SettingsStyles';
import { ITraitDefinition, QueueConfiguration } from '../../../services';
import { MaxTraitField } from '../MaxTraitField';
import { TraitTreeComponent } from '../../shared/TraitTreeComponent';
import { SalusPrimaryButton } from '../../shared/SalusPrimaryButton';
import { AdminContext } from '../../../Store/Admin/AdminProvider';

interface TraitsTabProps {
  queueData: QueueConfiguration;
  changeTab: React.Dispatch<React.SetStateAction<string>>;
}

export const TraitsTab: React.FC<TraitsTabProps> = props => {
  const { palette } = getTheme();
  const [traitDefDocFile, setTraitDefDocFile] = useState<File>();
  const inputFile = useRef<HTMLInputElement>(null);
  const { dispatch } = useContext(AdminContext);
  useEffect(() => {
    if (!traitDefDocFile) {
      return;
    }

    const fileStream = traitDefDocFile.text();

    fileStream
      .then(content => {
        let fileAsObj: Record<string, Record<string, ITraitDefinition>>;

        try {
          fileAsObj = JSON.parse(content) as Record<string, Record<string, ITraitDefinition>>;
        } catch (error) {
          alert('there was an error uploading the file, please insure the json is formatted correctly');
          return;
        }

        const errorMsg = verifyTraitDefinitions(fileAsObj);

        if (errorMsg) {
          alert(errorMsg);
          return;
        }

        dispatch({ type: 'CATEGORIZE_TRAIT_DEFINITIONS', payload: fileAsObj });
        dispatch({ type: 'IS_UPDATED', payload: true });
      })
      .catch((error: Error) => {
        alert(`There was an error streaming this file upload: ${error.message}`);
      });
  }, [traitDefDocFile]);

  const onFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length !== 1 || !event.target.files[0]) {
      alert('must upload one and only one file');
      return;
    }

    const theFile = event.target.files[0];

    if (theFile.type !== 'application/json') {
      alert('must upload json file');
    }

    setTraitDefDocFile(theFile);
  };

  const verifyTraitDefinitions = (newTraitDefJson: Record<string, Record<string, ITraitDefinition>>) => {
    if (!newTraitDefJson) {
      return 'No File to Verify';
    }

    const missingCategories: string[] = [];
    const missingTraits: string[] = [];
    const badTraitDefinition: string[] = [];
    const badTraitExamples: string[] = [];
    const badTraitExemptions: string[] = [];

    Object.keys(newTraitDefJson || []).forEach(newCategory => {
      if (Object.keys(props.queueData.categorizedTraits).includes(newCategory)) {
        Object.keys(newTraitDefJson?.[newCategory]).forEach(newTrait => {
          if (props.queueData.categorizedTraits[newCategory].includes(newTrait)) {
            const newTraitDef = newTraitDefJson[newCategory][newTrait];
            if (typeof newTraitDef.definition !== 'string') {
              badTraitDefinition.push(newTrait);
            } else if (typeof newTraitDef.examples[0] !== 'string' && typeof newTraitDef.examples[0] !== 'undefined') {
              badTraitExamples.push(newTrait);
            } else if (
              typeof newTraitDef.exemptions[0] !== 'string' &&
              typeof newTraitDef.exemptions[0] !== 'undefined'
            ) {
              badTraitExemptions.push(newTrait);
            }
          } else {
            missingTraits.push(newTrait);
          }
        });
      } else {
        missingCategories.push(newCategory);
      }
    });

    let returnString = '';
    if (
      missingCategories.length ||
      missingTraits.length ||
      badTraitDefinition.length ||
      badTraitExamples.length ||
      badTraitExemptions.length
    ) {
      if (inputFile.current) {
        inputFile.current.value = '';
      }

      if (missingCategories.length) {
        returnString =
          returnString +
          `You supplied these categories ${missingCategories.join(
            ' '
          )} that are missing from this queues category list. `;
      }
      if (missingTraits.length) {
        returnString =
          returnString +
          `You supplied these traits: ${missingTraits.join(' | ')} that are missing from this queues trait list. `;
      }
      if (badTraitDefinition.length) {
        returnString =
          returnString +
          `The trait definitions for these traits ${badTraitDefinition.join(' | ')} are missing or not strings. `;
      }
      if (badTraitExamples.length) {
        returnString =
          returnString +
          `The trait examples for these traits ${badTraitExamples.join(' | ')} are missing or not a list of strings. `;
      }
      if (badTraitExemptions.length) {
        returnString =
          returnString +
          `The trait exemptions for these traits ${badTraitExemptions.join(
            ' | '
          )} are missing or not a list of strings.`;
      }
    }

    return returnString;
  };

  const createJsonToDownload = (): string => {
    // @ts-ignore
    const beautifulFile = beautify(props.queueData.categorizedTraitDefinitions, null, 4, 60);
    return URL.createObjectURL(new Blob([beautifulFile], { type: 'application/json' }));
  };

  return (
    <BaseTab>
      <FieldSectionComponent
        title={'Max number of traits allowed'}
        titleAs={'h2'}
        body={'The maximum number of traits moderators can apply to a case when reviewing. Minimum 1, maximum 10.'}
      >
        <Stack.Item>
          <MaxTraitField tooltip={''} id="maxAllowedLabels" label="" />
        </Stack.Item>
      </FieldSectionComponent>
      <Separator styles={separatorStyle(palette)} />
      <Stack horizontal tokens={{ childrenGap: 97 }} style={{ justifyContent: 'flex-end', display: 'flex' }}>
        <Stack>
          <Text as={'h2'} className="ms-sm12 ms-md12 font-weight-bold" variant={'mediumPlus'}>
            {'Trait Definitions'}
          </Text>
          <Text className="ms-sm12 ms-md12" variant={'medium'}>
            {
              'Trait definitions includes a brief summary of what an individual trait means, a list of examples that should receive that trait, and a list of exemptions that might seem the same, but should not receive that trait.'
            }
          </Text>
        </Stack>
        <Stack horizontal>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              width: '220px',
              paddingRight: '5px'
            }}
          >
            <DefaultButton
              iconProps={{ iconName: 'Download' }}
              disabled={!props.queueData.categorizedTraitDefinitions}
              style={{
                alignSelf: 'center',
                borderRadius: '2px',
                width: '220px'
              }}
              href={createJsonToDownload()}
              download={'rulesDoc.json'}
            >
              {'Download Definitions'}
            </DefaultButton>
            <input type="file" onChange={onFileUpload} ref={inputFile} accept="application/json" hidden />
            <SalusPrimaryButton
              iconProps={{ iconName: 'Upload' }}
              style={{
                alignSelf: 'center',
                borderRadius: '2px',
                width: '220px'
              }}
              onClick={() => {
                if (inputFile.current) {
                  inputFile.current.click();
                }
              }}
            >
              {'Upload Definitions'}
            </SalusPrimaryButton>
          </div>
        </Stack>
      </Stack>
      <Separator styles={separatorStyle(palette)} />
      <FieldSectionComponent
        title={'Current traits'}
        titleAs={'h2'}
        body={
          'The traits that moderators can apply to a case when reviewing. If these require updates, contact support.'
        }
      ></FieldSectionComponent>
      <TraitTreeComponent
        categorizedTraits={props.queueData.categorizedTraits}
        categorizedTraitDefinitions={props.queueData.categorizedTraitDefinitions}
        changeTab={props.changeTab}
      />
    </BaseTab>
  );
};
